/**
 * Mid-playback stall watchdog for clips on the hls.js path (NEX-9360).
 *
 * On H.264/TS, starvation followed by recovery can pin `currentTime` just before a segment join
 * with the buffer full on both tracks: readyState 2, not paused, no hls.js error and no `waiting`
 * event. A tiny seek past the join resumes it, so this polls instead of listening for events.
 *
 * Framework-free and hls.js-free: it only watches a `<video>`. The caller decides what giving up
 * means (ClipPlayer falls back a codec, then to mp4).
 */

const HAVE_METADATA = 1

// A frame-accurate pin keeps currentTime identical; anything above this counts as advancing.
const ADVANCE_EPSILON_S = 0.001

// Range starts can sit a hair after currentTime after a nudge or a codec switch.
const RANGE_START_TOLERANCE_S = 0.1

// Clean playback needed before the per-load nudge budget refills.
const NUDGE_RESET_AFTER_MS = 10_000

/** Seconds buffered past currentTime in the range that contains it; 0 when none does. */
export function bufferedAheadOf(video) {
    const buffered = video?.buffered
    if (!buffered) {
        return 0
    }

    const time = video.currentTime
    for (let i = 0; i < buffered.length; i++) {
        const start = buffered.start(i)
        const end = buffered.end(i)
        if (time >= start - RANGE_START_TOLERANCE_S && time <= end) {
            return Math.max(0, end - time)
        }
    }

    return 0
}

/**
 * Watches `video` while started. When playback should be running but `currentTime` has not
 * moved for `windowMs` with at least `minBufferAheadS` buffered ahead, it nudges `currentTime`
 * forward by `nudgeS`. Past `maxNudges` within one load it calls `onGiveUp` and stops itself.
 *
 * True starvation (little buffered ahead) is left to hls.js; seeking, paused and ended are
 * never acted on. `onStall` is optional and fires on every detected stall, before the nudge or
 * the give-up. Every callback gets `{ currentTime, bufferedAhead, nudges }`.
 */
export function createStallWatchdog(video, {
    windowMs = 2000,
    minBufferAheadS = 1,
    nudgeS = 0.1,
    maxNudges = 3,
    pollMs = 500,
    onNudge = () => {},
    onGiveUp = () => {},
    onStall = () => {},
} = {}) {
    let timer = null

    // Per-load state, cleared by reset().
    let nudgesThisLoad = 0
    let lastTime = null
    let lastAdvanceAt = 0
    let lastPollAt = 0
    let cleanPlaybackMs = 0

    // Lifetime totals, for stats() and the debug hook.
    const totals = { stalls: 0, nudges: 0, fallbacks: 0 }

    const now = () => Date.now()

    const info = () => ({
        currentTime: video.currentTime,
        bufferedAhead: bufferedAheadOf(video),
        nudges: nudgesThisLoad,
    })

    // Restart the stall window from the current position, e.g. after a pause or seek.
    const rearm = (at) => {
        lastTime = video.currentTime
        lastAdvanceAt = at
    }

    const poll = () => {
        const at = now()
        const sincePoll = at - lastPollAt
        lastPollAt = at

        if (video.paused || video.seeking || video.ended || video.readyState < HAVE_METADATA) {
            rearm(at)
            return
        }

        if (lastTime === null || Math.abs(video.currentTime - lastTime) > ADVANCE_EPSILON_S) {
            const advanced = lastTime !== null
            rearm(at)
            if (advanced) {
                cleanPlaybackMs += sincePoll
                if (cleanPlaybackMs >= NUDGE_RESET_AFTER_MS) {
                    nudgesThisLoad = 0
                }
            }
            return
        }

        if (at - lastAdvanceAt < windowMs || bufferedAheadOf(video) < minBufferAheadS) {
            return
        }

        totals.stalls++
        cleanPlaybackMs = 0
        onStall(info())

        if (nudgesThisLoad >= maxNudges) {
            totals.fallbacks++
            const detail = info()
            stop()
            onGiveUp(detail)
            return
        }

        nudgesThisLoad++
        totals.nudges++
        video.currentTime = video.currentTime + nudgeS
        // Give the nudge a full window to take before judging it.
        rearm(at)
        onNudge(info())
    }

    function start() {
        if (timer !== null || !video) {
            return
        }
        lastPollAt = now()
        rearm(lastPollAt)
        timer = setInterval(poll, pollMs)
    }

    function stop() {
        if (timer !== null) {
            clearInterval(timer)
            timer = null
        }
    }

    /** Clears the per-load nudge budget and stall window. Lifetime totals are kept. */
    function reset() {
        nudgesThisLoad = 0
        cleanPlaybackMs = 0
        lastPollAt = now()
        lastTime = null
        lastAdvanceAt = lastPollAt
    }

    function stats() {
        return { ...totals }
    }

    return { start, stop, reset, stats }
}
